import { useContext, useEffect, useMemo } from 'react';
import { LegalDescriptionModel } from '@urbanx/agx-ui-components';
import { LoadingState } from 'utils/loadingState';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { updateFormValue } from 'features/form/formReducer';
import { fetchTitleReferences, resetTitleReference } from 'features/rpData/rpDataReducer';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import useFormAddressSelector from 'selectors/useFormAddressSelector';

const createParcelInputSignature = (parcels?: LegalDescriptionModel['parcels']) =>
  (parcels ?? [])
    .map((parcel) =>
      [parcel.lotNumber ?? '', parcel.planType ?? '', parcel.planNumber ?? ''].join('|')
    )
    .join('||');

const LegalDescriptionPreloader = () => {
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();

  const campaignId = useAppSelector((state) => state.form.campaignId);
  const {
    loadingState: titleReferenceLoadingState,
    items: parcels,
    campaignId: cachedCampaignId,
  } = useAppSelector((state) => state.rpData.titleReference);

  const legalDescription = useAppSelector<LegalDescriptionModel>(
    (state) => state.form.formValues.LegalDescription
  );

  const propertyDetails = useFormPropertyDetailsSelector();
  const propertyAddress = useFormAddressSelector();
  const legalParcelInputSignature = useMemo(
    () => createParcelInputSignature(legalDescription?.parcels),
    [legalDescription?.parcels]
  );
  const cachedParcelInputSignature = useMemo(() => createParcelInputSignature(parcels), [parcels]);

  const shouldSkipFetchingTitleReference = useMemo(
    () =>
      parcels?.some((parcel, index) => {
        const legalParcel = legalDescription?.parcels?.[index];
        return (
          (!parcel.titleReference && legalParcel?.titleReference) ||
          (parcel.titleReference &&
            legalParcel?.titleReference &&
            parcel.titleReference !== legalParcel?.titleReference) ||
          parcel.lotNumber !== legalParcel?.lotNumber ||
          parcel.planNumber !== legalParcel?.planNumber ||
          parcel.planType !== legalParcel?.planType
        );
      }),
    [parcels, legalDescription?.parcels]
  );

  useEffect(() => {
    if (
      titleReferenceLoadingState === LoadingState.NotLoaded ||
      titleReferenceLoadingState === LoadingState.Loading ||
      cachedCampaignId !== campaignId ||
      legalParcelInputSignature === cachedParcelInputSignature
    ) {
      return;
    }

    const resetTimeout = window.setTimeout(() => {
      dispatch(resetTitleReference());
    }, 350);

    return () => window.clearTimeout(resetTimeout);
  }, [
    titleReferenceLoadingState,
    cachedCampaignId,
    campaignId,
    legalParcelInputSignature,
    cachedParcelInputSignature,
    dispatch,
  ]);

  useEffect(() => {
    if (
      titleReferenceLoadingState !== LoadingState.Loaded ||
      !parcels?.length ||
      shouldSkipFetchingTitleReference ||
      !legalDescription?.parcels?.length ||
      parcels?.length !== legalDescription?.parcels?.length ||
      cachedCampaignId !== campaignId
    ) {
      return;
    }

    const hasChanges = parcels.some((parcel, index) => {
      const localCopy = legalDescription.parcels[index];
      return (
        parcel.lotNumber !== localCopy?.lotNumber ||
        parcel.planType !== localCopy?.planType ||
        parcel.planNumber !== localCopy?.planNumber ||
        parcel.titleReference !== localCopy?.titleReference
      );
    });

    if (!hasChanges) return;

    dispatch(
      updateFormValue({
        id: 'LegalDescription',
        value: {
          ...legalDescription,
          parcels: parcels,
        },
      })
    );
  }, [
    titleReferenceLoadingState,
    campaignId,
    cachedCampaignId,
    parcels,
    shouldSkipFetchingTitleReference,
    legalDescription,
    dispatch,
  ]);

  useEffect(() => {
    if (cachedCampaignId !== campaignId) {
      dispatch(resetTitleReference());
    }
  }, [cachedCampaignId, campaignId, dispatch]);

  useEffect(() => {
    const { propertyId, parcels } = propertyDetails;

    if (
      !getAuthToken ||
      titleReferenceLoadingState !== LoadingState.NotLoaded ||
      (!propertyId && !propertyAddress?.state) ||
      !campaignId ||
      !parcels
    )
      return;

    getAuthToken().then(async (authToken) => {
      if (!authToken) return;

      dispatch(
        fetchTitleReferences(
          authToken,
          campaignId,
          parcels,
          propertyId?.toString(),
          propertyAddress?.state
        )
      );
    });
  }, [
    campaignId,
    titleReferenceLoadingState,
    propertyDetails?.parcels,
    propertyDetails?.propertyId,
  ]);

  return <></>;
};

export default LegalDescriptionPreloader;
